// @ts-nocheck
/*
 * Port of views/dashboard/agent-status-distribution.js.
 * Zero-API — pure presentation. Data comes from props (parent DashboardPage).
 * Visual identity preserved: dual-panel (bar + donut), same colour map,
 * same tooltip / legend / rounded-bar shape. Outer shell uses shadcn Card,
 * refresh button uses shadcn Button + lucide RefreshCw.
 */
import { RefreshCw } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const STATUS_COLORS: Record<string, string> = {
  available: '#10b981',
  Available: '#10b981',
  busy: '#f43f5e',
  Busy: '#f43f5e',
  offline: '#94a3b8',
  Offline: '#94a3b8',
};

const FALLBACK_COLORS = ['#10b981', '#f43f5e', '#94a3b8', '#6366f1', '#f97316', '#0ea5e9'];

function getColor(entry, index) {
  return STATUS_COLORS[entry.status] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

function DashboardChartTooltip({ active, payload, label }) {
  const isDark =
    typeof document !== 'undefined' && document.body.classList.contains('dark');

  if (!active || !payload || !payload.length) return null;

  return (
    <div
      style={{
        background: isDark ? 'rgba(17,24,39,0.92)' : 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: isDark
          ? '1px solid rgba(148,163,184,0.18)'
          : '1px solid rgba(15,23,42,0.10)',
        borderRadius: 12,
        padding: '10px 14px',
        boxShadow: isDark
          ? '0 12px 32px rgba(2,6,23,0.45)'
          : '0 12px 32px rgba(15,23,42,0.12)',
        minWidth: 100,
      }}
    >
      {label && (
        <div
          style={{
            fontWeight: 700,
            fontSize: 12,
            marginBottom: 6,
            color: isDark ? '#f1f5f9' : '#0f172a',
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </div>
      )}
      {payload.map((entry, i) => (
        <div
          key={`${entry.name}-${i}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            color: isDark ? '#e2e8f0' : '#1e293b',
            marginTop: i > 0 ? 4 : 0,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: entry.color || entry.fill || '#6366f1',
              flexShrink: 0,
              display: 'inline-block',
            }}
          />
          <span style={{ fontWeight: 600 }}>{entry.name}:</span>
          <span>{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

function RoundedBar(props) {
  const { x, y, width, height, fill } = props;
  if (!height || height <= 0) return null;
  const r = Math.min(6, width / 2);
  return (
    <path
      d={`M${x},${y + height} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${y + height} Z`}
      fill={fill}
    />
  );
}

function CustomLegend({ data }) {
  return (
    <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1.5">
      {data.map((entry, i) => (
        <span
          key={entry.status}
          className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground"
        >
          <span
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ background: getColor(entry, i) }}
          />
          {entry.status}
        </span>
      ))}
    </div>
  );
}

export default function AgentStatusDistribution({ graphData, onRefresh, loading }) {
  const safeData = Array.isArray(graphData) ? graphData : [];
  const totalAgents = safeData.reduce((sum, d) => sum + (d.agents || 0), 0);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-sm text-muted-foreground">
          Loading agent status…
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-start justify-between border-b border-border px-6 py-4">
        <div>
          <div className="text-base font-semibold text-foreground">
            Agent Status Distribution
          </div>
          <div className="text-xs font-medium text-muted-foreground">
            Live agent availability overview
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={onRefresh}
            title="Refresh Agent Status Distribution"
            aria-label="Refresh Agent Status Distribution"
            className="h-8 w-8 rounded-full"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <span className="whitespace-nowrap rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
            {totalAgents} agents
          </span>
        </div>
      </div>

      {/* Body — bar + donut */}
      <div className="flex flex-wrap px-2 py-5">
        {/* Bar */}
        <div className="min-w-0 flex-1 basis-[280px] px-3">
          <div className="mb-2 pl-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            By Status — Bar
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={safeData}
              margin={{ top: 10, right: 16, left: -10, bottom: 4 }}
              barCategoryGap="35%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis
                dataKey="status"
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))', fontWeight: 600 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<DashboardChartTooltip />} cursor={{ fill: 'hsl(var(--primary) / 0.1)' }} />
              <Bar dataKey="agents" name="Agents" shape={<RoundedBar />}>
                {safeData.map((entry, index) => (
                  <Cell key={`bar-${index}`} fill={getColor(entry, index)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <CustomLegend data={safeData} />
        </div>

        <div className="mx-1 my-2.5 w-px shrink-0 self-stretch bg-border" />

        {/* Donut */}
        <div className="min-w-0 flex-1 basis-[280px] px-3">
          <div className="mb-2 pl-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Distribution — Donut
          </div>
          <div className="relative">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={safeData}
                  dataKey="agents"
                  nameKey="status"
                  cx="50%"
                  cy="48%"
                  innerRadius={72}
                  outerRadius={110}
                  paddingAngle={3}
                  startAngle={90}
                  endAngle={-270}
                >
                  {safeData.map((entry, index) => (
                    <Cell key={`pie-${index}`} fill={getColor(entry, index)} stroke="transparent" />
                  ))}
                </Pie>
                <Tooltip content={<DashboardChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>

            <div
              className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[58%] text-center"
            >
              <div className="text-[28px] font-extrabold leading-none text-foreground">
                {totalAgents}
              </div>
              <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Total
              </div>
            </div>
          </div>
          <CustomLegend data={safeData} />
        </div>
      </div>
    </>
  );
}
